import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Container } from "@mui/material";

import {
  ActionIcon,
  Badge,
  Button,
  CopyButton,
  Divider,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Table,
  Text,
  Textarea,
  Tooltip,
  TypographyStylesProvider,
} from "@mantine/core";

import { GridRenderCellParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import DatagridDisplay from "../../Components/DatagridDisplay";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import MegaLoader from "../../Components/MegaLoader";
import { BrandImages, Colors } from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  getEllipsisFromString,
  getPaginationTotal,
} from "../../Lib/Methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/Responses";
import {
  PaginationProps,
  TicketCategoryOptions,
  TicketProps,
  TicketResponseProps,
  TransactionProps,
  UserProfileProps,
  UserSubscriptionProps,
} from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";
import {
  IconArrowBack,
  IconExternalLink,
  IconReload,
} from "@tabler/icons-react";
import DisplayAlert from "../../Components/DisplayAlert";

const SupportTickets: React.FC = () => {
  const navigate = useNavigate();
  const responseForm = useForm({
    initialValues: {
      message: "",
    },
    validate: {
      message: (value) =>
        value && value.length >= 10 ? null : "At least 10 characters!",
    },
  });
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: tickets,
    reloadData: reloadTickets,
    isLoading: isLoadingTickets,
  } = usePerformRequest<PaginatedResponse<TicketProps[]>>({
    method: "GET",
    route: Endpoints.GetUsersTickets(pagination.page, pagination.limit),
  });

  const [
    isTicketModalOpen,
    { open: OpenTicketModal, close: CloseTicketModal },
  ] = useDisclosure(false);

  const [currentTicket, setCurrentTicket] = useState<TicketProps>();

  const [currentReplies, setCurrentReplies] = useState<TicketResponseProps[]>(
    []
  );
  const [isLoadingTicketStatus, setLoadingTicketStatus] =
    useState<boolean>(false);
  const ToggleTicketStatus = async () => {
    if (currentTicket) {
      setLoadingTicketStatus(true);
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetUpdateTicketStatus(currentTicket.id),
        data: {
          status: currentTicket.status === "resolved" ? "pending" : "resolved",
        },
        callback: () => {
          setLoadingTicketStatus(false);
          DefaultErrorNotification("Could not update ticket status");
        },
      });
      setLoadingTicketStatus(false);
      if (r && r.status === 200) {
        DefaultSuccessNotification("Ticket has been updated");
        reloadTickets();
        CloseTicketModal();
      }
    }
  };

  const [isResponding, setResponding] = useState<boolean>(false);
  const SendResponse = async () => {
    if (currentTicket) {
      const { hasErrors } = responseForm.validate();
      if (!hasErrors) {
        setResponding(true);
        const r = await PerformRequest({
          method: "POST",
          route: Endpoints.GetRespondToTicket(currentTicket.id),
          data: {
            message: responseForm.values.message,
          },
          callback: () => {
            setResponding(false);
            DefaultErrorNotification("Could not send response");
          },
        });
        setResponding(false);
        if (r) {
          GetTicketReplyHistory();
          responseForm.reset();
        }
      }
    }
  };
  const [isGetTicketReplyHistoryLoading, setGetTicketReplyHistoryLoading] =
    useState<boolean>(false);
  const GetTicketReplyHistory = async () => {
    if (currentTicket) {
      setGetTicketReplyHistoryLoading(true);
      const r = await PerformRequest<
        NonPaginatedResponse<TicketResponseProps[]>
      >({
        method: "GET",
        route: Endpoints.GetTicketReplies(currentTicket.id),
        callback: () => {
          setGetTicketReplyHistoryLoading(false);
          DefaultErrorNotification("Could not fetch ticket replies");
        },
      });
      setGetTicketReplyHistoryLoading(false);
      if (r && r.data) {
        setCurrentReplies(r.data.data);
      }
    }
  };
  useEffect(() => {
    if (currentTicket && currentTicket.id) {
      GetTicketReplyHistory();
    }
  }, [currentTicket]);

  const [isLoadingUser, setLoadingUser] = useState<boolean>(false);
  const [isLoadingSubscription, setLoadingSubscription] =
    useState<boolean>(false);
  const [isLoadingTransaction, setLoadingTransaction] =
    useState<boolean>(false);

  const [currentUser, setCurrentUser] = useState<UserProfileProps>();

  const [currentSubscriptionID, setCurrentSubscriptionID] =
    useState<string>("");
  const [currentTransactionID, setCurrentTransactionID] = useState<string>("");
  const [currentSubscription, setCurrentSubscription] =
    useState<UserSubscriptionProps>();

  const [currentTransaction, setCurrentTransaction] =
    useState<TransactionProps>();

  const [
    isSubscriptionModalOpened,
    { open: OpenSubscriptionModal, close: CloseSubscriptionModal },
  ] = useDisclosure(false);
  const [
    isTransactionModalOpened,
    { open: OpenTransactionModal, close: CloseTransactionModal },
  ] = useDisclosure(false);

  useEffect(() => {
    const GetSubscriptionDetails = async () => {
      if (currentSubscriptionID && currentSubscriptionID.length > 0) {
        setLoadingSubscription(true);

        const r = await PerformRequest<
          NonPaginatedResponse<UserSubscriptionProps>
        >({
          method: "GET",
          route: Endpoints.GetSingleSubscription(currentSubscriptionID),
          callback: () => {
            setLoadingSubscription(false);
          },
        });
        setLoadingSubscription(false);
        if (r) {
          if (r.status === 404) {
            DefaultErrorNotification("Subscription does not exist!");
          }
          if (r.status === 200) {
            setCurrentSubscription(r.data.data);
          }
        }
      }
    };

    GetSubscriptionDetails();
  }, [currentSubscriptionID]);
  useEffect(() => {
    const GetTransactionDetails = async () => {
      if (currentTransactionID && currentTransactionID.length > 0) {
        setLoadingTransaction(true);

        const r = await PerformRequest<NonPaginatedResponse<TransactionProps>>({
          method: "GET",
          route: Endpoints.GetSingleTransaction(currentTransactionID),
          callback: () => {
            setLoadingTransaction(false);
          },
        });
        setLoadingTransaction(false);
        if (r) {
          if (r.status === 404) {
            DefaultErrorNotification("Transaction does not exist!");
          }
          if (r.status === 200) {
            setCurrentTransaction(r.data.data);
          }
        }
      }
    };

    GetTransactionDetails();
  }, [currentTransactionID]);
  useEffect(() => {
    const GetUserProfile = async () => {
      setLoadingUser(true);
      if (currentTicket && currentTicket.userID) {
        const r = await PerformRequest<NonPaginatedResponse<UserProfileProps>>({
          method: "GET",
          route: Endpoints.GetUserProfile(currentTicket.userID),
          callback: () => {
            setLoadingUser(false);
          },
        });
        setLoadingUser(false);
        if (r) {
          if (r.status === 404) {
            DefaultErrorNotification("User does not exist!");
          }
          if (r.status === 200) {
            setCurrentUser(r.data.data);
          }
        }
      }
    };
    GetUserProfile();
  }, [currentSubscriptionID, currentTransactionID]);
  return (
    <Container>
      <div className="support-tickets-container">
        <div className="component-header">
          <div className="flex-row align-center justify-between width-100">
            <Text className="label">Support Tickets</Text>
            <Button
              loading={isLoadingTickets}
              onClick={reloadTickets}
              leftSection={<IconReload size={15} />}
            >
              Reload
            </Button>
          </div>
          <br />

          <Modal
            opened={isTransactionModalOpened}
            onClose={() => {
              CloseTransactionModal();
              OpenTicketModal();
            }}
            title="Transaction Details"
            centered
            size="lg"
            closeButtonProps={{
              icon: <IconArrowBack color={Colors.BluePrimary} />,
            }}
          >
            {currentTransaction ? (
              <>
                <Table variant="vertical" layout="fixed" withTableBorder>
                  <Table.Thead>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th w={200}>Transaction ID</Table.Th>
                      <Table.Td>
                        <CopyButton value={currentTransaction.id}>
                          {({ copied, copy }) => (
                            <Tooltip
                              label={copied ? "Copied!" : "Copy to clipboard"}
                            >
                              <div
                                className="pointer flex-row align-center"
                                onClick={copy}
                                style={{
                                  color: copied
                                    ? Colors.GreenPrimary
                                    : Colors.BluePrimary,
                                  height: "100%",
                                }}
                              >
                                {currentTransaction.id}
                              </div>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Date</Table.Th>
                      <Table.Td>
                        {dayjs(currentTransaction.createdAt).format(
                          "DD MMM YYYY - hh:mmA"
                        )}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Service</Table.Th>
                      <Table.Td>
                        <div className="flex-row align-center temp-service-images">
                          {currentTransaction.services.map((service) => {
                            return (
                              <img
                                src={BrandImages.retrieveImage(service)}
                                alt=""
                                className="image"
                              />
                            );
                          })}
                        </div>
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>User</Table.Th>
                      <Table.Td>
                        {currentUser?.name ?? <Loader size={15} />}
                      </Table.Td>
                    </Table.Tr>
                  </Table.Thead>
                </Table>

                <Divider w="100%" mt={15} mb={10} />
                <Text fw={500} mb={10} c={Colors.GreenSecondary}>
                  User Details
                </Text>
                {currentUser && currentUser && currentUser.id ? (
                  <>
                    <Table variant="vertical" layout="fixed" withTableBorder>
                      <Table.Thead>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>User ID</Table.Th>
                          <Table.Td>{currentUser.id}</Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Full Name</Table.Th>
                          <Table.Td>{currentUser.name}</Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Date Joined</Table.Th>
                          <Table.Td>
                            {dayjs(currentUser.createdAt).format(
                              "DD MMM YYYY - hh:mmA"
                            )}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Email</Table.Th>
                          <Table.Td>
                            <CopyButton value={currentUser.email}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied
                                      ? "Email Copied!"
                                      : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {currentUser.email}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Promoter</Table.Th>
                          <Table.Td>
                            <Badge
                              color={
                                currentUser.isPromoter
                                  ? Colors.GreenPrimary
                                  : Colors.BluePrimary
                              }
                            >
                              {currentUser.isPromoter ? "YES" : "NO"}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      </Table.Thead>
                    </Table>
                  </>
                ) : (
                  <>
                    {isLoadingUser ? (
                      <div className="flex-row align-center justify-center width-100">
                        <Loader my={20} size={34} />
                      </div>
                    ) : (
                      <DisplayAlert
                        title="No User Found"
                        children={
                          <Text>
                            For some reason, this user does not exist :-(
                          </Text>
                        }
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="flex-row align-center justify-center">
                <MegaLoader />
              </div>
            )}
          </Modal>
          <Modal
            opened={isSubscriptionModalOpened}
            onClose={() => {
              CloseSubscriptionModal();
              OpenTicketModal();
            }}
            title="Subscription Details"
            centered
            size="lg"
            closeButtonProps={{
              icon: <IconArrowBack color={Colors.BluePrimary} />,
            }}
          >
            {currentSubscription ? (
              <>
                <Table variant="vertical" layout="fixed" withTableBorder>
                  <Table.Thead>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th w={200}>Transaction ID</Table.Th>
                      <Table.Td>
                        <CopyButton value={currentSubscription.transactionID}>
                          {({ copied, copy }) => (
                            <Tooltip
                              label={copied ? "Copied!" : "Copy to clipboard"}
                            >
                              <div
                                className="pointer flex-row align-center"
                                onClick={copy}
                                style={{
                                  color: copied
                                    ? Colors.GreenPrimary
                                    : Colors.BluePrimary,
                                  height: "100%",
                                }}
                              >
                                {currentSubscription.transactionID}
                              </div>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Date</Table.Th>
                      <Table.Td>
                        {dayjs(currentSubscription.createdAt).format(
                          "DD MMM YYYY - hh:mmA"
                        )}
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Service</Table.Th>
                      <Table.Td>
                        <div className="flex-row align-center temp-service-images">
                          <img
                            src={BrandImages.retrieveImage(
                              currentSubscription.service
                            )}
                            alt=""
                            className="image"
                          />
                        </div>
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>User</Table.Th>
                      <Table.Td>
                        {currentUser?.name ?? <Loader size={15} />}
                      </Table.Td>
                    </Table.Tr>
                  </Table.Thead>
                </Table>

                <Divider w="100%" mt={15} mb={10} />
                <Text fw={500} mb={10} c={Colors.GreenSecondary}>
                  User Details
                </Text>
                {currentUser && currentUser && currentUser.id ? (
                  <>
                    <Table variant="vertical" layout="fixed" withTableBorder>
                      <Table.Thead>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>User ID</Table.Th>
                          <Table.Td>{currentUser.id}</Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Full Name</Table.Th>
                          <Table.Td>{currentUser.name}</Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Date Joined</Table.Th>
                          <Table.Td>
                            {dayjs(currentUser.createdAt).format(
                              "DD MMM YYYY - hh:mmA"
                            )}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Email</Table.Th>
                          <Table.Td>
                            <CopyButton value={currentUser.email}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied
                                      ? "Email Copied!"
                                      : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {currentUser.email}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Promoter</Table.Th>
                          <Table.Td>
                            <Badge
                              color={
                                currentUser.isPromoter
                                  ? Colors.GreenPrimary
                                  : Colors.BluePrimary
                              }
                            >
                              {currentUser.isPromoter ? "YES" : "NO"}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      </Table.Thead>
                    </Table>
                  </>
                ) : (
                  <>
                    {isLoadingUser ? (
                      <div className="flex-row align-center justify-center width-100">
                        <Loader my={20} size={34} />
                      </div>
                    ) : (
                      <DisplayAlert
                        title="No User Found"
                        children={
                          <Text>
                            For some reason, this user does not exist :-(
                          </Text>
                        }
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="flex-row align-center justify-center">
                <MegaLoader />
              </div>
            )}
          </Modal>

          {currentTicket && (
            <Modal
              opened={isTicketModalOpen}
              onClose={() => {
                CloseTicketModal();
                setCurrentReplies([]);
                responseForm.reset();

                setCurrentTicket(undefined);
              }}
              title="Ticket Details"
              centered
              size="xl"
            >
              <Table variant="vertical" layout="fixed" withTableBorder>
                <Table.Thead>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Category</Table.Th>
                    <Table.Td tt="capitalize">
                      {currentTicket?.category.replaceAll("-", " ")}
                    </Table.Td>
                  </Table.Tr>
                  {currentTicket.subscriptionID && (
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Subscription</Table.Th>
                      <Table.Td
                        className="pointer"
                        onClick={() => {
                          CloseTicketModal();
                          OpenSubscriptionModal();
                          setCurrentSubscriptionID(
                            currentTicket.subscriptionID as string
                          );
                        }}
                        c={Colors.BluePrimary}
                      >
                        {currentTicket?.subscriptionID}
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {currentTicket.transactionID && (
                    <Table.Tr key={generateRandomString(12)}>
                      <Table.Th>Transaction</Table.Th>
                      <Table.Td
                        className="pointer"
                        onClick={() => {
                          CloseTicketModal();
                          OpenTransactionModal();
                          setCurrentTransactionID(
                            currentTicket.transactionID as string
                          );
                        }}
                        c={Colors.BluePrimary}
                      >
                        {currentTicket?.transactionID}
                      </Table.Td>
                    </Table.Tr>
                  )}
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Title</Table.Th>
                    <Table.Td>
                      {getEllipsisFromString(currentTicket?.title, 30)}
                    </Table.Td>
                  </Table.Tr>

                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Date Logged</Table.Th>
                    <Table.Td>
                      {dayjs(currentTicket.createdAt).format(
                        "DD MMM YYYY - hh:mmA"
                      )}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Last Updated</Table.Th>
                    <Table.Td>
                      {currentTicket.updatedAt ? (
                        <>
                          {dayjs(currentTicket.updatedAt).format("DD MMM YYYY")}
                        </>
                      ) : (
                        <>Unattended</>
                      )}
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr key={generateRandomString(12)}>
                    <Table.Th>Status</Table.Th>
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={
                          currentTicket.status === "resolved"
                            ? Colors.GreenPrimary
                            : Colors.OrangeSecondary
                        }
                      >
                        {currentTicket.status}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                </Table.Thead>
              </Table>
              <Text my={10} fw={500}>
                Initial Message
              </Text>
              <Text lh={2.2}>{currentTicket.message}</Text>
              <Divider w="100%" my={10} />
              <Text my={10} fw={500}>
                Message History
              </Text>

              <div className="history-container">
                {isGetTicketReplyHistoryLoading && (
                  <MegaLoader
                    containerStyle={{
                      marginTop: 20,
                      marginBottom: 20,
                    }}
                  />
                )}
                {currentReplies.length === 0 && (
                  <Text c="dimmed" fz="sm">
                    No replies yet
                  </Text>
                )}
                {currentReplies.map((reply) => {
                  return (
                    <Paper
                      mb={10}
                      withBorder
                      radius="md"
                      p={10}
                      style={{
                        borderColor:
                          reply.sender === "user"
                            ? Colors.BluePrimary
                            : Colors.GreenPrimary,
                      }}
                    >
                      <Group>
                        <div>
                          <Text fz="sm">
                            {reply.sender === "user" ? "User" : "Admin"}
                          </Text>
                          <Text fz="xs" c="dimmed">
                            {dayjs(reply.createdAt).format(
                              "DD MMM YYYY - hh:mmA"
                            )}
                          </Text>
                        </div>
                      </Group>
                      <Text fz="sm">{reply.message}</Text>
                    </Paper>
                  );
                })}
              </div>

              <Divider w="100%" my={10} />
              <Textarea
                label="Response"
                placeholder="Enter your response here"
                spellCheck={false}
                mb={20}
                {...responseForm.getInputProps("message")}
              />
              <div className="flex-row align-center">
                <Button
                  color={Colors.BluePrimary}
                  mr={10}
                  disabled={!responseForm.isValid()}
                  onClick={SendResponse}
                  loading={isResponding}
                >
                  Send Response
                </Button>
                <Button
                  variant="outline"
                  loading={isLoadingTicketStatus}
                  color={
                    currentTicket.status === "resolved"
                      ? Colors.OrangeSecondary
                      : Colors.GreenPrimary
                  }
                  tt="capitalize"
                  onClick={ToggleTicketStatus}
                >
                  Mark as{" "}
                  {currentTicket.status === "resolved" ? "pending" : "resolved"}
                </Button>
              </div>
            </Modal>
          )}
          {tickets && tickets.data && (
            <>
              <DatagridDisplay
                rows={tickets?.data as any}
                isLoading={isLoadingTickets}
                onRowClick={(params) => {
                  OpenTicketModal();
                  setCurrentTicket(params.row);
                }}
                columns={[
                  {
                    field: "createdAt",
                    width: 150,

                    headerName: "Date Logged",
                    renderCell: (params: GridRenderCellParams<TicketProps>) => {
                      return (
                        <div>
                          {dayjs(params.row.createdAt).format("DD MMM YYYY")}
                        </div>
                      );
                    },
                  },
                  {
                    field: "updatedAt",
                    width: 150,

                    headerName: "Last Updated",
                    renderCell: (params: GridRenderCellParams<TicketProps>) => {
                      return (
                        <div>
                          {params.row.updatedAt ? (
                            <>
                              {dayjs(params.row.updatedAt).format(
                                "DD MMM YYYY"
                              )}
                            </>
                          ) : (
                            <>Unattended</>
                          )}
                        </div>
                      );
                    },
                  },
                  {
                    field: "title",
                    flex: 1,

                    headerName: "Title",
                    renderCell: (params: GridRenderCellParams<TicketProps>) => {
                      return (
                        <div>
                          {getEllipsisFromString(params.row?.title, 60)}
                        </div>
                      );
                    },
                  },
                  {
                    field: "category",
                    flex: 1,

                    headerName: "Title",
                    renderCell: (params: GridRenderCellParams<TicketProps>) => {
                      return (
                        <div className="capitalize">
                          {params.row.category.replaceAll("-", " ")}
                        </div>
                      );
                    },
                  },
                  {
                    field: "status",
                    headerName: "Status",
                    width: 120,
                    renderCell: (params: GridRenderCellParams<TicketProps>) => {
                      return (
                        <Badge
                          size="sm"
                          color={
                            params.row.status === "resolved"
                              ? Colors.GreenPrimary
                              : Colors.OrangeSecondary
                          }
                        >
                          {params.row.status}
                        </Badge>
                      );
                    },
                  },
                ]}
              />
              <div className="flex-row align-center justify-center width-100">
                <Pagination
                  value={pagination.page}
                  onChange={(value) => {
                    setPagination({ ...pagination, page: value });
                  }}
                  my={40}
                  disabled={tickets?.data.length === 0 || isLoadingTickets}
                  total={getPaginationTotal(
                    tickets?.totalRecords,
                    pagination.limit
                  )}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Container>
  );
};

export default SupportTickets;
